// Procedural, recolourable base-style SVG engine. viewBox is in METRES so a
// pattern stays consistent across turf sizes. Mirrored in backend svgbuilder.py
// so the live preview and production file match.
import { TRAD_CAMO_INNER } from "./traditionalCamoSvg";

export const SCALE = { small: 0.7, medium: 1.0, large: 1.5 };
const TSCALE = { small: 0.6, medium: 1.0, large: 1.7 };
const R = (x) => Math.round(x * 1000) / 1000;

// ---- colour helpers (auto mid-tone for camo depth: Camo only) ----
function hx(c) {
  let h = (c || "#000").replace("#", "");
  if (h.length === 3) h = h.split("").map((x) => x + x).join("");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) || 0);
}
function toHex(rgb) {
  return "#" + rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");
}
function mix(a, b, t) { const A = hx(a), B = hx(b); return toHex(A.map((v, i) => v + (B[i] - v) * t)); }
export function midTone(primary, secondary) { return mix(secondary, primary, 0.42); }

// ===== Traditional Camo — EXACT customer-supplied master SVG (1000x220), =====
// recoloured by data-role fill (secondary base, mid silhouettes, primary shapes)
// and tiled at uniform scale so shapes are never distorted.
function traditionalCamo(p, s, mid, u) {
  const art = TRAD_CAMO_INNER.split("#FFFFFF").join(s).split("#F3A7C7").join(mid).split("#D94B8D").join(p);
  const g = (2.0 * u) / 220;          // width dimension anchors 2m*scale; uniform => no distortion
  const tw = 220 * g, tl = 1000 * g;
  return `<pattern id="cm" width="${tl}" height="${tw}" patternUnits="userSpaceOnUse"><g transform="scale(${g})">${art}</g></pattern>`;
}

// ===== Digital Camo — clustered pixel camo (author 400x400 tile) =====
const CAMO_DIGITAL = {
  s: [[4,0],[5,0],[6,0],[7,0],[8,0],[9,0],[10,0],[15,0],[4,1],[5,1],[6,1],[7,1],[10,1],[15,1],[17,1],[4,2],[5,2],[6,2],[7,2],[13,2],[15,2],[16,2],[1,3],[2,3],[3,3],[4,3],[5,3],[6,3],[7,3],[13,3],[14,3],[15,3],[2,4],[3,4],[4,4],[5,4],[6,4],[7,4],[13,4],[14,4],[1,5],[2,5],[3,5],[4,5],[5,5],[6,5],[7,5],[11,5],[3,6],[6,6],[7,6],[10,6],[11,6],[12,6],[0,7],[1,7],[1,8],[9,8],[10,8],[12,8],[13,9],[11,10],[12,10],[13,10],[14,10],[4,11],[5,11],[9,11],[12,11],[13,11],[14,11],[15,11],[3,12],[4,12],[5,12],[6,12],[8,12],[9,12],[10,12],[11,12],[12,12],[13,12],[1,13],[2,13],[3,13],[4,13],[5,13],[7,13],[8,13],[9,13],[10,13],[11,13],[12,13],[13,13],[14,13],[1,14],[2,14],[5,14],[6,14],[7,14],[8,14],[9,14],[10,14],[11,14],[12,14],[13,14],[14,14],[15,14],[1,15],[6,15],[7,15],[8,15],[9,15],[10,15],[11,15],[12,15],[13,15],[14,15],[1,16],[2,16],[6,16],[7,16],[8,16],[9,16],[10,16],[11,16],[13,16],[14,16],[15,16],[0,17],[1,17],[2,17],[3,17],[4,17],[6,17],[7,17],[8,17],[9,17],[10,17],[11,17],[0,18],[1,18],[2,18],[3,18],[4,18],[5,18],[6,18],[7,18],[8,18],[9,18],[10,18],[1,19],[3,19],[4,19],[5,19],[8,19],[10,19],[11,19]],
  mid: [[0,0],[1,0],[2,0],[3,0],[11,0],[12,0],[13,0],[17,0],[18,0],[19,0],[0,1],[1,1],[2,1],[3,1],[11,1],[12,1],[13,1],[14,1],[18,1],[19,1],[0,2],[1,2],[2,2],[3,2],[11,2],[12,2],[18,2],[19,2],[0,3],[11,3],[12,3],[16,3],[17,3],[18,3],[19,3],[0,4],[1,4],[9,4],[10,4],[11,4],[12,4],[15,4],[16,4],[17,4],[18,4],[19,4],[0,5],[8,5],[9,5],[10,5],[16,5],[17,5],[18,5],[19,5],[0,6],[1,6],[2,6],[9,6],[18,6],[19,6],[2,7],[3,7],[4,7],[9,7],[10,7],[11,7],[12,7],[18,7],[19,7],[2,8],[3,8],[4,8],[11,8],[2,9],[3,9],[4,9],[8,9],[9,9],[10,9],[11,9],[12,9],[2,10],[3,10],[4,10],[8,10],[9,10],[10,10],[12,17],[13,17],[14,17],[15,17],[11,18],[12,18],[13,18],[14,18],[0,19],[2,19],[12,19],[19,19]],
};
function digitalInner(p, s, mid) {
  const cell = 20;
  const cells = (arr, c) => arr.map(([col, row]) => `<rect x="${col * cell}" y="${row * cell}" width="${cell + 0.5}" height="${cell + 0.5}" fill="${c}"/>`).join("");
  return `<rect x="-20" y="-20" width="440" height="440" fill="${p}"/>` + cells(CAMO_DIGITAL.s, s) + cells(CAMO_DIGITAL.mid, mid);
}
function digitalCamo(p, s, mid, u) {
  const tileM = R(3.0 * u), g = R(tileM / 400);
  return `<pattern id="cm" width="${tileM}" height="${tileM}" patternUnits="userSpaceOnUse"><g transform="scale(${g})">${digitalInner(p, s, mid)}</g></pattern>`;
}

function camoBuild(key, p, s, u) {
  const mid = midTone(p, s);
  if (key === "camo-digital") return digitalCamo(p, s, mid, u);
  return traditionalCamo(p, s, mid, u); // camo-traditional (and any legacy camo key)
}

// ---- futuristic (two-colour, unchanged this iteration) ----
function angularTech(p, s) {
  const bands = ["0,2 1.4,0 2.0,0 0.6,2", "2.4,2 3.8,0 4.0,0.35 2.75,2", "0,0.5 1.1,0.5 0.55,1.5 -0.55,1.5"];
  const lines = ["M2.1,0 L3.2,2", "M3.4,0.2 L4,1.3"];
  return `<pattern id="cm" width="4" height="2" patternUnits="userSpaceOnUse"><rect width="4" height="2" fill="${p}"/>`
    + bands.map((pt) => `<polygon points="${pt}" fill="${s}"/>`).join("")
    + lines.map((d) => `<path d="${d}" stroke="${s}" stroke-width="0.08" fill="none"/>`).join("") + `</pattern>`;
}
function techCircuit(p, s) {
  const paths = ["M0,0.6 L0.8,0.6 L1.2,1.0 L2.0,1.0", "M0.4,1.5 L1.1,1.5 L1.4,1.2 L2.2,1.2", "M2.0,0.3 L2.6,0.3 L3.0,0.7 L3.0,1.6", "M2.4,1.7 L2.9,1.7"];
  const dashes = [[0.2,0.85],[1.7,0.35],[2.7,1.4],[0.9,1.15]];
  let out = `<pattern id="cm" width="3" height="2" patternUnits="userSpaceOnUse"><rect width="3" height="2" fill="${p}"/>`;
  out += paths.map((d) => `<path d="${d}" stroke="${s}" stroke-width="0.1" fill="none"/>`).join("");
  out += dashes.map(([x, y]) => [0,1,2].map((i) => `<rect x="${R(x+i*0.11)}" y="${y}" width="0.06" height="0.18" fill="${s}"/>`).join("")).join("");
  out += `<polygon points="${hexPts(1.6,1.6,0.28)}" fill="none" stroke="${s}" stroke-width="0.05"/></pattern>`;
  return out;
}
function futureBlock(p, s) {
  const rects = ["0.1,0.1,1.1,0.7", "1.4,0.2,1.3,0.5", "0.2,1.0,0.9,0.8", "1.3,0.9,1.4,0.9"];
  return `<pattern id="cm" width="3" height="2" patternUnits="userSpaceOnUse"><rect width="3" height="2" fill="${p}"/>`
    + rects.map((r) => { const [x, y, w, h] = r.split(",").map(Number); return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="0.08" fill="none" stroke="${s}" stroke-width="0.07"/>`; }).join("") + `</pattern>`;
}
function speedTech(p, s) {
  const ch = [];
  for (let i = 0; i < 4; i++) { const o = i * 0.8; ch.push(`M${o},2 L${o+0.9},0 L${o+1.1},0 L${o+0.2},2 Z`); }
  return `<pattern id="cm" width="3.2" height="2" patternUnits="userSpaceOnUse"><rect width="3.2" height="2" fill="${p}"/>`
    + ch.map((d) => `<path d="${d}" fill="${s}"/>`).join("") + `</pattern>`;
}

function hexPts(cx, cy, a) {
  const h = Math.sqrt(3) * a / 2;
  return `${R(cx + a)},${R(cy)} ${R(cx + a / 2)},${R(cy + h)} ${R(cx - a / 2)},${R(cy + h)} ${R(cx - a)},${R(cy)} ${R(cx - a / 2)},${R(cy - h)} ${R(cx + a / 2)},${R(cy - h)}`;
}
function hexGrid(p, s, L, W, u) {
  const a = 0.42 * u, stepX = 1.5 * a, stepY = Math.sqrt(3) * a;
  let out = `<rect width="${L}" height="${W}" fill="${p}"/>`;
  for (let c = -1; c * stepX < L + a; c++) for (let r = -1; r * stepY < W + stepY; r++) {
    const cx = c * stepX, cy = r * stepY + (c % 2 ? stepY / 2 : 0);
    out += `<polygon points="${hexPts(cx, cy, a)}" fill="none" stroke="${s}" stroke-width="${R(0.045 * u)}"/>`;
  }
  return out;
}

const TECH = { "futuristic-angular": angularTech, "tech-circuit": techCircuit, "future-block": futureBlock, "speed-tech": speedTech };

export function buildBaseSvg(bs, L, W) {
  const p = bs?.primary || "#111111";
  const s = bs?.secondary || "#00D26A";
  const key = bs?.pattern || "solid";
  const u = SCALE[bs?.scale || "medium"];
  const uid = "cm" + Math.random().toString(36).slice(2, 9);
  const head = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${L} ${W}" preserveAspectRatio="none" width="100%" height="100%" style="position:absolute;inset:0;display:block">`;
  if (key === "solid") return `${head}<rect width="${L}" height="${W}" fill="${p}"/></svg>`;
  if (key === "hex") return `${head}${hexGrid(p, s, L, W, u)}</svg>`;
  if (key.startsWith("camo-")) {
    const pat = camoBuild(key, p, s, TSCALE[bs?.scale || "medium"]).replace('id="cm"', `id="${uid}"`);
    return `${head}<defs>${pat}</defs><rect width="${L}" height="${W}" fill="url(#${uid})"/></svg>`;
  }
  const fn = TECH[key];
  if (!fn) return `${head}<rect width="${L}" height="${W}" fill="${p}"/></svg>`;
  let pat = fn(p, s);
  if (u !== 1) pat = pat.replace('<pattern id="cm"', `<pattern id="cm" patternTransform="scale(${u})"`);
  pat = pat.replace('id="cm"', `id="${uid}"`);
  return `${head}<defs>${pat}</defs><rect width="${L}" height="${W}" fill="url(#${uid})"/></svg>`;
}

// Realistic short artificial-grass fibre texture: luminance-only overlay (dark fibre shadows + light fibre tips)
// so it tints with whatever base colour/pattern sits beneath. Units are metres (viewBox) and the container
// keeps the true L:W ratio, so no geometry distortion. Mirrored in backend svgbuilder.turf_texture_svg.
export function buildTurfTextureSvg(L, W, unitsPerM = 1) {
  const uid = "tx" + Math.random().toString(36).slice(2, 9);
  const f = (perM) => (perM / unitsPerM).toFixed(4);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${L * unitsPerM} ${W * unitsPerM}" preserveAspectRatio="none" width="100%" height="100%" style="position:absolute;inset:0;display:block">
<defs>
<filter id="${uid}d" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
  <feTurbulence type="fractalNoise" baseFrequency="${f(55)} ${f(22)}" numOctaves="3" seed="11" stitchTiles="stitch" result="n"/>
  <feColorMatrix in="n" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  1.6 0 0 0 -0.55" result="a"/>
  <feFlood flood-color="#000" flood-opacity="0.34"/><feComposite in2="a" operator="in"/>
</filter>
<filter id="${uid}l" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
  <feTurbulence type="fractalNoise" baseFrequency="${f(140)} ${f(48)}" numOctaves="2" seed="29" stitchTiles="stitch" result="n"/>
  <feColorMatrix in="n" type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  1.9 0 0 0 -0.95" result="a"/>
  <feFlood flood-color="#fff" flood-opacity="0.16"/><feComposite in2="a" operator="in"/>
</filter>
<filter id="${uid}g" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
  <feTurbulence type="fractalNoise" baseFrequency="${f(9)} ${f(9)}" numOctaves="2" seed="5" stitchTiles="stitch" result="n"/>
  <feColorMatrix in="n" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0.9 0 0 0 -0.3" result="a"/>
  <feFlood flood-color="#000" flood-opacity="0.14"/><feComposite in2="a" operator="in"/>
</filter>
</defs>
<rect width="100%" height="100%" filter="url(#${uid}g)"/>
<rect width="100%" height="100%" filter="url(#${uid}d)"/>
<rect width="100%" height="100%" filter="url(#${uid}l)"/>
</svg>`;
}

// Reusable "turf material": the same three fibre layers as buildTurfTextureSvg, composited INTO the element's
// own alpha (feComposite in SourceGraphic) so the grass fibres appear only inside the element's real pixels —
// lines, numbers, borders, shapes, text, logos with transparency. pxPerM keeps fibre scale identical to the base.
export function turfMaterialFilterSvg(id, pxPerM) {
  const f = (perM) => (perM / pxPerM).toFixed(5);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="0" height="0" style="position:absolute;width:0;height:0" aria-hidden="true"><defs>
<filter id="${id}" x="0" y="0" width="100%" height="100%" color-interpolation-filters="sRGB">
  <feTurbulence type="fractalNoise" baseFrequency="${f(9)} ${f(9)}" numOctaves="2" seed="5" stitchTiles="stitch" result="n0"/>
  <feColorMatrix in="n0" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0.9 0 0 0 -0.3" result="a0"/>
  <feFlood flood-color="#000" flood-opacity="0.10" result="c0"/><feComposite in="c0" in2="a0" operator="in" result="grain"/>
  <feTurbulence type="fractalNoise" baseFrequency="${f(55)} ${f(22)}" numOctaves="3" seed="11" stitchTiles="stitch" result="n1"/>
  <feColorMatrix in="n1" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  1.6 0 0 0 -0.55" result="a1"/>
  <feFlood flood-color="#000" flood-opacity="0.26" result="c1"/><feComposite in="c1" in2="a1" operator="in" result="dark"/>
  <feTurbulence type="fractalNoise" baseFrequency="${f(140)} ${f(48)}" numOctaves="2" seed="29" stitchTiles="stitch" result="n2"/>
  <feColorMatrix in="n2" type="matrix" values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  1.9 0 0 0 -0.95" result="a2"/>
  <feFlood flood-color="#fff" flood-opacity="0.12" result="c2"/><feComposite in="c2" in2="a2" operator="in" result="light"/>
  <feMerge result="tex"><feMergeNode in="SourceGraphic"/><feMergeNode in="grain"/><feMergeNode in="dark"/><feMergeNode in="light"/></feMerge>
  <feComposite in="tex" in2="SourceGraphic" operator="in"/>
</filter></defs></svg>`;
}
